import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CircleCheckBig } from 'lucide-react';
import { formatNumber } from 'helpers';
import {
  CATEGORY_STANDARD,
  CATEGORY_PREMIUM,
  CATEGORY_NON_RESIDENT_STANDARD,
  CATEGORY_NON_RESIDENT_PREMIUM,
  defaultCategories,
  categoriesDetails,
} from 'data';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';

const CategoriesTable = ({
  categories = null,
  isSelectable = false,
  isPilgrimUser = false,
  selectedCategories = [],
  setSelectedCategories = null,
  variant = 'default',
}) => {
  const [rows, setRows] = useState([
    { label: 'Prix (CFA)', key: 'price' },
    { label: 'Transport aérien', key: 'transport' },
    { label: 'Hébergements \n (Médine & Mecque)', key: 'mecca' },
    { label: 'Service à Mina & Arafat', key: 'service' },
    { label: 'Pièces obligatoires', key: 'documents' },
  ]);

  const mobileRows = ['price', 'actions'];
  const isComparison = variant === 'comparison';

  const getCategory = (categoryName) => categories.find((cat) => cat.name === categoryName);

  const categoryStandardId = getCategory(CATEGORY_STANDARD)?.id;

  const categoryPremiumId = getCategory(CATEGORY_PREMIUM)?.id;

  const categoryDiasporaStandardId = getCategory(CATEGORY_NON_RESIDENT_STANDARD)?.id;

  const categoryDiasporaPremiumId = getCategory(CATEGORY_NON_RESIDENT_PREMIUM)?.id;

  const allowedCategories = categories.map((cat) => cat.name);

  // Filter default categories to only those available
  const displayedCategories = defaultCategories.filter((cat) => allowedCategories.includes(cat));

  // Premium → Standard dependency
  const categoriesDependencies = {
    [categoryPremiumId]: categoryStandardId,
    [categoryDiasporaPremiumId]: categoryDiasporaStandardId,
  };

  const isSelectedCategory = (categoryId) => selectedCategories.includes(categoryId);

  const toggleCategory = (id) => {
    setSelectedCategories((prev) => {
      const isSelected = prev.includes(id);

      // --- REMOVE ---
      if (isSelected) {
        return prev.filter((x) => x !== id);
      }

      // --- ADD ---
      let next = [...prev, id];

      // If this category requires a base category, ensure it's added
      if (categoriesDependencies[id]) {
        const requiredId = categoriesDependencies[id];
        if (!next.includes(requiredId)) {
          next.push(requiredId);
        }
      }

      return next;
    });
  };

  useEffect(() => {
    if (isSelectable) {
      setRows((prevData) => {
        const alreadyExists = prevData.some((row) => row.label === 'Actions');
        if (alreadyExists) return prevData;

        return [...prevData, { label: 'Actions', key: 'actions' }];
      });
    } else {
      setRows((prevMethods) => prevMethods.filter((method) => method.label !== 'Actions'));
    }
  }, [isSelectable]);

  return (
    <Table
      containerClassName={cn(isComparison && 'h-full overscroll-contain [scrollbar-width:thin]')}
      className={cn(
        'w-full border-separate border-spacing-0',
        isComparison ? 'min-w-[940px] table-fixed text-sm text-gray-800' : 'rounded-lg border border-gray-300'
      )}
    >
      <TableHeader className={cn(isComparison && 'bg-[#f6f8f6]')}>
        <TableRow className={cn(isComparison && 'hover:bg-transparent')}>
          <TableHead
            className={cn(
              'w-48 bg-gray-50',
              isComparison
                ? 'sticky left-0 top-0 z-20 h-auto w-[182px] border-b border-r border-[#d6dfd8] bg-[#f6f8f6] px-[18px] py-[18px] text-left text-xs font-normal text-[#738078]'
                : 'rounded-tl-lg border border-gray-300'
            )}
          >
            Services / Catégories
          </TableHead>
          {displayedCategories.map((categorie, idx) => (
            <TableHead
              key={categorie}
              className={cn(
                'bg-gray-50 text-center',
                isComparison
                  ? 'sticky top-0 z-10 h-auto border-b border-[#d6dfd8] px-3.5 py-[18px] font-semibold text-[#334a3b]'
                  : 'border border-gray-300',
                !isComparison && idx === displayedCategories.length - 1 && 'rounded-tr-lg',
                isComparison &&
                  isSelectable &&
                  isSelectedCategory(getCategory(categorie)?.id) &&
                  'bg-[#eff7f2] shadow-[inset_0_-3px_#007e61]'
              )}
            >
              {categorie}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>

      <TableBody>
        {rows.map((row, rowIdx) => (
          <TableRow key={row.key} className={cn(isComparison && 'hover:bg-transparent')}>
            {/* Row label */}
            <TableCell
              className={cn(
                'bg-gray-50 font-medium whitespace-pre-line',
                isComparison
                  ? 'sticky left-0 z-10 border-b border-r border-[#e1e7e2] bg-[#f8faf8] px-[18px] py-4 align-middle text-[#495b50] shadow-[1px_0_0_#e1e7e2]'
                  : 'border border-gray-300',
                !isComparison && rowIdx === rows.length - 1 && 'rounded-bl-lg',
                !isComparison && !mobileRows.includes(row.key) && 'hidden lg:table-cell'
              )}
            >
              {isComparison && row.key === 'price'
                ? 'Prix'
                : isComparison && row.key === 'actions'
                ? 'Action'
                : row.label}
            </TableCell>

            {/* Dynamic category values */}
            {displayedCategories.map((key, colIdx) => {
              const value = categoriesDetails[key][row.key];
              const curentCategory = getCategory(key);

              return (
                <TableCell
                  key={key}
                  className={cn(
                    'align-top whitespace-pre-line',
                    isComparison
                      ? 'border-b border-[#e6eae7] px-[18px] py-4 text-left leading-6'
                      : 'border border-gray-300',
                    isComparison && row.key === 'price' && 'bg-[#fcfdfb] text-center align-middle',
                    isComparison && row.key === 'actions' && 'bg-[#f5f9f5] align-middle',
                    !isComparison &&
                      rowIdx === rows.length - 1 &&
                      colIdx === displayedCategories.length - 1 &&
                      'rounded-br-lg',
                    !isComparison && !mobileRows.includes(row.key) && 'hidden lg:table-cell'
                  )}
                >
                  {/* Arrays = show bullet list */}
                  {Array.isArray(value) && (
                    <>
                      {value.length > 0 && (
                        <ul className={cn('list-disc pl-4', isComparison && 'space-y-1.5 marker:text-[#637568]')}>
                          {value.map((doc, i) => (
                            <li key={i}>{doc}</li>
                          ))}
                        </ul>
                      )}

                      {value.length === 0 && (
                        <p className={cn(isComparison && 'text-center text-gray-500')}>{isComparison ? '—' : '-'}</p>
                      )}
                    </>
                  )}

                  {/* Actions column */}
                  {row.key === 'actions' && (
                    <Button
                      variant="outline"
                      className={cn(
                        'border-hajj-primary min-w-[120px] w-full',
                        isComparison && 'h-[34px] min-w-0 border-[#b7ccbe] px-3 text-[13px] shadow-none',
                        isComparison && isSelectedCategory(curentCategory?.id) && 'border-hajj-primary bg-[#e8f4ed]'
                      )}
                      onClick={() => toggleCategory(curentCategory?.id)}
                      aria-pressed={isSelectedCategory(curentCategory?.id)}
                      aria-label={`${
                        isSelectedCategory(curentCategory?.id) ? 'Désélectionner' : 'Sélectionner'
                      } ${key}`}
                    >
                      {isSelectedCategory(curentCategory?.id) && (
                        <>
                          <CircleCheckBig className="text-hajj-primary" />
                          <span className="text-hajj-primary">Sélectionnée</span>
                        </>
                      )}
                      {!isSelectedCategory(curentCategory?.id) && <p className="text-hajj-primary">Sélectionner</p>}
                    </Button>
                  )}

                  {/*  Price → formatted number */}
                  {row.key === 'price' && (
                    <div className="text-center">
                      <p
                        className={cn(
                          'text-xl text-hajj-primary font-bold',
                          isComparison && 'text-[17px] font-semibold tabular-nums'
                        )}
                      >
                        {isPilgrimUser
                          ? formatNumber(curentCategory?.price.public || 0)
                          : formatNumber(curentCategory?.price.sa || 0)}
                      </p>
                      {isComparison && (
                        <span className="mt-0.5 block text-[11px] font-normal text-[#738378]">F CFA</span>
                      )}
                    </div>
                  )}

                  {/* Normal values (strings) */}
                  {!Array.isArray(value) && row.key !== 'actions' && row.key !== 'price' && (
                    <p className={cn('whitespace-pre-line', !isComparison && 'text-center')}>{value}</p>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

CategoriesTable.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  isSelectable: PropTypes.bool,
  isPilgrimUser: PropTypes.bool,
  selectedCategories: PropTypes.arrayOf(PropTypes.string),
  setSelectedCategories: PropTypes.func,
  variant: PropTypes.oneOf(['default', 'comparison']),
};

export default CategoriesTable;
